import { PillGroup, SelectBox } from '@homex/se-react-components';
import { CarouselItemId } from '@homex/se-widget-flow-types';
import moment from 'moment-timezone';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setCurrentCarouselItem,
  setSelectedSchedulePreference,
  setSelectedSchedulePreferenceDay,
  setSelectedSchedulePreferenceTime,
  setSelectedTimeSlot,
} from 'actions';
import { CarouselContinueButton } from 'components/CarouselContinueButton';
import { Phrase, interpolateString } from 'components/Phrase';
import { useStringContext } from 'components/StringContext';
import { ReactComponent as CalendarFlipSvg } from 'components/svg/CalendarFlip.svg';
import { ReactComponent as ClockSvg } from 'components/svg/Clock.svg';
import { usePhoneId } from 'hooks/usePhoneId';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  IState,
  SchedulePreference,
  SchedulePreferenceTime,
  ServiceType,
  StartTimePreference,
} from 'typings';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  Container,
  Description,
  FormGroup,
  PreferredTimeContainer,
  Subhead,
  TimeSelectBox,
} from './styles';

/**
 * NOTE: This component is also use for ServiceType.SALES, but that will be
 * deprecated in the future.
 */
export const CAROUSEL_ITEM_ID = CarouselItemId.SchedulePreference;

export const CarouselItemSchedulePreferenceBottom = () => {
  // TODO: Move this to CarouselItemRepairSummaryBottomContainer when it is
  // refactored into a functional component. This is a temporary fix for #653.
  usePhoneId();

  const dispatch = useThunkDispatch();
  const {
    flowCarouselItem,
    selectedSchedulePreference,
    selectedSchedulePreferenceDay,
    selectedSchedulePreferenceTime,
    serviceType,
    serviceZoneTimezone = '',
  } = useSelector((state: IState) => ({
    flowCarouselItem: tryGetFlowCarouselItem(
      state.client.flow,
      CAROUSEL_ITEM_ID,
    ),
    selectedSchedulePreference: state.userInput.selectedSchedulePreference,
    selectedSchedulePreferenceDay:
      state.userInput.selectedSchedulePreferenceDay,
    selectedSchedulePreferenceTime:
      state.userInput.selectedSchedulePreferenceTime,
    serviceType: state.userInput.serviceType || ServiceType.REPAIR,
    serviceZoneTimezone: state.userInput.serviceZoneTimezone,
  }));

  const strings = useStringContext();
  const theme = useTheme();

  const schedulePreferenceOptions = [
    {
      key: SchedulePreference.FIRST_AVAILABLE,
      label: Phrase({ id: 'SCHEDULE_PREFERENCE.FIRST_AVAILABLE' }),
      value: SchedulePreference.FIRST_AVAILABLE,
    },
    {
      key: SchedulePreference.PREFERRED_TIME,
      label: Phrase({ id: 'SCHEDULE_PREFERENCE.PREFERRED_TIME' }),
      value: SchedulePreference.PREFERRED_TIME,
    },
  ];

  // The values here correspond to weekday indexes starting with Sunday as 0. We can easily pass this into moment
  // to get the next matching day in the current week.
  const dayOptions = [
    { key: 'Monday', label: strings['DAY.MONDAY'] ?? 'Monday', value: '1' },
    { key: 'Tuesday', label: strings['DAY.TUESDAY'] ?? 'Tuesday', value: '2' },
    {
      key: 'Wednesday',
      label: strings['DAY.WEDNESDAY'] ?? 'Wednesday',
      value: '3',
    },
    {
      key: 'Thursday',
      label: strings['DAY.THURSDAY'] ?? 'Thursday',
      value: '4',
    },
    { key: 'Friday', label: strings['DAY.FRIDAY'] ?? 'Friday', value: '5' },
  ];

  const timeOptions = [
    {
      key: 'Morning',
      label: strings['TIME.MORNING'] ?? 'Morning',
      value: StartTimePreference.MORNING,
    },
    {
      key: 'Afternoon',
      label: strings['TIME.AFTERNOON'] ?? 'Afternoon',
      value: StartTimePreference.AFTERNOON,
    },
  ];

  const handleContinueClick = () => {
    let label = '';
    let time = '';

    if (selectedSchedulePreference === SchedulePreference.FIRST_AVAILABLE) {
      label = strings['SCHEDULE_PREFERENCE.FIRST_AVAILABLE'] ?? '';
      time = moment.tz(serviceZoneTimezone).endOf('day').format();
    } else if (selectedSchedulePreferenceDay) {
      let momentDate;

      // moment().day(number) returns the day of the current week. But if we are past that day in the current week
      // we want to use next week's day. For example, today is Thursday the 9th and the user selects "Monday" in
      // the dropdown, moment().day(1) will return Monday the 6th rather than Monday the 13th so we add 7
      if (
        moment.tz(serviceZoneTimezone).day() > selectedSchedulePreferenceDay
      ) {
        momentDate = moment
          .tz(serviceZoneTimezone)
          .day(selectedSchedulePreferenceDay + 7)
          .endOf('day');
      } else {
        momentDate = moment
          .tz(serviceZoneTimezone)
          .day(selectedSchedulePreferenceDay)
          .endOf('day');
      }

      label = interpolateString(
        strings['SCHEDULE_PREFERENCE.LABEL.TEMPLATE'] ?? '',
        {
          DAY_OF_WEEK: momentDate.format('dddd'),
          TIME_OF_DAY: strings[`TIME.${selectedSchedulePreferenceTime}`] ?? '',
        },
      );
      time = momentDate.format();
    }

    const timeSlot = {
      contact: null,
      endTime: time,
      label,
      startTime: time,
    };

    let destinationItemId;

    if (flowCarouselItem) {
      destinationItemId = flowCarouselItem.options.destinations.next;
    } else {
      destinationItemId =
        theme.enterpriseEnabled && theme.enterpriseVersion === '2'
          ? CarouselItemId.AvailablePartners
          : serviceType === ServiceType.REPAIR
          ? CarouselItemId.RepairSummary
          : CarouselItemId.SystemEquipment;
    }

    dispatch(setSelectedTimeSlot(timeSlot));
    dispatch(
      setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationItemId),
    );
  };

  const handleSchedulePreferenceChange = (
    event: React.FormEvent<HTMLInputElement>,
  ) => {
    dispatch(
      setSelectedSchedulePreference(
        event.currentTarget.value as SchedulePreference,
      ),
    );
  };

  const handleSchedulePreferenceChangeDay = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    if (value) {
      dispatch(setSelectedSchedulePreferenceDay(parseInt(value, 10)));
    } else {
      dispatch(setSelectedSchedulePreferenceDay());
    }
  };

  const handleSchedulePreferenceChangeTime = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.currentTarget;

    if (value) {
      dispatch(
        setSelectedSchedulePreferenceTime(value as SchedulePreferenceTime),
      );
    } else {
      dispatch(setSelectedSchedulePreferenceTime());
    }
  };

  const isContinueDisabled = () => {
    const isValid =
      selectedSchedulePreference === SchedulePreference.FIRST_AVAILABLE ||
      (selectedSchedulePreference === SchedulePreference.PREFERRED_TIME &&
        selectedSchedulePreferenceDay &&
        selectedSchedulePreferenceTime);

    return !isValid;
  };

  const renderDescription = () => {
    let phrase = '';

    if (!selectedSchedulePreference) {
      phrase = strings['SCHEDULE_PREFERENCE.DESCRIPTION.DEFAULT'] ?? '';
    } else if (
      selectedSchedulePreference === SchedulePreference.FIRST_AVAILABLE
    ) {
      phrase = strings['SCHEDULE_PREFERENCE.DESCRIPTION.FIRST_AVAILABLE'] ?? '';
    } else if (
      selectedSchedulePreferenceDay &&
      selectedSchedulePreferenceTime
    ) {
      phrase = strings['SCHEDULE_PREFERENCE.DESCRIPTION.PREFERRED_TIME'] ?? '';
    }

    return <Description>{phrase}</Description>;
  };

  return (
    <FormGroup>
      <Container>
        <Subhead>
          <Phrase id="SCHEDULE_PREFERENCE.SUBHEAD" />
        </Subhead>
        <PillGroup
          onChange={handleSchedulePreferenceChange}
          options={schedulePreferenceOptions}
          size="small"
          value={selectedSchedulePreference}
        />
        <PreferredTimeContainer>
          {selectedSchedulePreference === SchedulePreference.PREFERRED_TIME && (
            <>
              <SelectBox
                defaultLabel=""
                label={Phrase({ id: 'SCHEDULE_PREFERENCE.DAY_OF_WEEK' })}
                leftIcon={CalendarFlipSvg}
                onChange={handleSchedulePreferenceChangeDay}
                options={dayOptions}
                value={selectedSchedulePreferenceDay}
              />
              {selectedSchedulePreferenceDay && (
                <TimeSelectBox
                  defaultLabel=""
                  label={Phrase({ id: 'SCHEDULE_PREFERENCE.TIME_OF_DAY' })}
                  leftIcon={ClockSvg}
                  onChange={handleSchedulePreferenceChangeTime}
                  options={timeOptions}
                  value={selectedSchedulePreferenceTime}
                />
              )}
            </>
          )}
          {renderDescription()}
        </PreferredTimeContainer>
      </Container>
      <CarouselContinueButton
        isDisabled={isContinueDisabled()}
        onClick={handleContinueClick}
      />
    </FormGroup>
  );
};
